/**
 * Structured-data helpers.
 *
 * Two jobs:
 *  1. `pruneJsonLd`  — strip the values that make Google's Rich Results /
 *     Schema.org validators complain: undefined, null, empty strings,
 *     empty arrays and empty objects. React/Helmet happily serialises
 *     `"telephone": null`, and validators flag it as an invalid value.
 *  2. `serializeJsonLd` — emit the JSON safely inside a <script> tag.
 *     A raw `</script>` or a lone `<` inside any string field breaks the
 *     surrounding HTML and the whole block is silently dropped.
 *
 * `validateJsonLd` runs the same rules the validators apply, so the
 * schema can be checked in CI (see scripts/validate-structured-data.mjs)
 * instead of discovering breakage in Search Console weeks later.
 */

const EMPTY = Symbol("empty");

function prune(value) {
  if (value === null || value === undefined) return EMPTY;

  if (Array.isArray(value)) {
    const items = value.map(prune).filter((item) => item !== EMPTY);
    return items.length ? items : EMPTY;
  }

  if (typeof value === "object") {
    const out = {};
    Object.keys(value).forEach((key) => {
      const pruned = prune(value[key]);
      if (pruned !== EMPTY) out[key] = pruned;
    });
    // A node with only "@type" (or nothing) carries no information.
    const meaningful = Object.keys(out).filter(
      (key) => key !== "@type" && key !== "@context"
    );
    return meaningful.length ? out : EMPTY;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : EMPTY;
  }

  if (typeof value === "number" && !Number.isFinite(value)) return EMPTY;

  return value;
}

export function pruneJsonLd(schema) {
  const pruned = prune(schema);
  return pruned === EMPTY ? null : pruned;
}

/** Escape the characters that can terminate the host <script> element. */
export function serializeJsonLd(schema) {
  const cleaned = pruneJsonLd(schema);
  if (!cleaned) return null;
  return JSON.stringify(cleaned)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}

const ABSOLUTE_URL = /^https?:\/\/[^\s]+$/i;
const URL_KEYS = new Set(["url", "@id", "sameAs", "logo", "image", "item", "contentUrl"]);

/**
 * Returns a list of human-readable problems. An empty list means the
 * graph is structurally valid for Schema.org consumers.
 */
export function validateJsonLd(schema, path = "root", issues = []) {
  if (schema === null || schema === undefined) {
    issues.push(`${path}: node is ${String(schema)}`);
    return issues;
  }

  if (Array.isArray(schema)) {
    schema.forEach((item, i) => validateJsonLd(item, `${path}[${i}]`, issues));
    return issues;
  }

  if (typeof schema !== "object") return issues;

  if (path === "root" && !schema["@context"]) {
    issues.push(`${path}: missing "@context"`);
  }
  if (!schema["@type"] && !schema["@id"]) {
    issues.push(`${path}: node has neither "@type" nor "@id" (dangling reference)`);
  }

  Object.entries(schema).forEach(([key, value]) => {
    const childPath = `${path}.${key}`;

    if (value === undefined || value === null) {
      issues.push(`${childPath}: empty value (${String(value)})`);
      return;
    }
    if (typeof value === "string" && !value.trim()) {
      issues.push(`${childPath}: empty string`);
      return;
    }
    if (Array.isArray(value) && value.length === 0) {
      issues.push(`${childPath}: empty array`);
      return;
    }

    if (URL_KEYS.has(key)) {
      const urls = Array.isArray(value) ? value : [value];
      urls.forEach((entry) => {
        if (typeof entry === "string" && !ABSOLUTE_URL.test(entry)) {
          issues.push(`${childPath}: "${entry}" is not an absolute http(s) URL`);
        }
      });
    }

    if (typeof value === "object") {
      validateJsonLd(value, childPath, issues);
    }
  });

  return issues;
}

const jsonLdHelpers = { pruneJsonLd, serializeJsonLd, validateJsonLd };

export default jsonLdHelpers;
